<template>
  <span class="edit-customer-wrapper">
    <app-button
      data-qa="open-customer-modal-button"
      button-class="action-primary"
      :disabled="disabled"
      @click="toggleModal(true)"
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        aria-hidden="true"
      >
        <path d="M12 20h9"></path>
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
      </svg>
      <span>{{ openModalButtonTitle }}</span>
    </app-button>
    <modal v-if="showModal" :title="openedModalTitle" @close="toggleModal(false)">
      <form @submit.prevent="updateCustomer">
        <app-fieldset title="" class="customer">
          <form-input
            id="firstname"
            v-model.lazy="editCustomer.CustomerFirstName"
            class="label customer-col-3"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit FirstName Label')"
            :error-message="getValidationsMessage('CustomerFirstName')"
            maxlength="50"
            type="text"
          />
          <form-input
            id="middlename"
            v-model.lazy="editCustomer.CustomerMiddleName"
            class="customer-col-3"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit MiddleName Label')"
            :error-message="getValidationsMessage('CustomerMiddleName')"
            maxlength="50"
            type="text"
          />
          <form-input
            id="initials"
            v-model.lazy="editCustomer.CustomerInitials"
            class="customer-col-3"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit Initials Label')"
            :error-message="getValidationsMessage('CustomerInitials')"
            maxlength="10"
            type="text"
          />
          <form-input
            id="familyname"
            v-model.lazy="editCustomer.CustomerFamilyName"
            class="customer-col-3"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit FamilyName Label')"
            :error-message="getValidationsMessage('CustomerFamilyName')"
            maxlength="50"
            type="text"
          />
          <app-form-field
            id="genderTypes"
            :title="i18n.t('/Customer/Create New Customer Form/New Customer Gender Label')"
            class="label customer-col-5 radio-button"
          >
            <form-radio-button
              v-for="genderType in genderTypes"
              :id="`gender-${genderType.value}`"
              :key="genderType.value"
              :title="genderType.label"
            >
              <input
                :id="`gender-${genderType.value}`"
                v-model="editCustomer.CustomerGender"
                :value="genderType.value"
                :data-qa="`gender-${genderType.value}`"
                name="gender"
                type="radio"
                :error-message="getValidationsMessage('CustomerGender')"
              />
            </form-radio-button>
          </app-form-field>
          <form-input
            id="emailaddress"
            v-model.lazy="editCustomer.CustomerEmail"
            class="customer-col-3"
            :title="i18n.t('/Customer/Customer Email Label')"
            maxlength="50"
            type="email"
            :error-message="getValidationsMessage('CustomerEmail')"
          />
          <form-input
            id="phone"
            v-model.lazy="editCustomer.CustomerPhone"
            class="customer-col-3"
            :title="i18n.t('/Customer/Customer Phone Label')"
            maxlength="50"
            type="text"
            :error-message="getValidationsMessage('CustomerPhone')"
          />
          <form-input
            id="zipcode"
            v-model.lazy="editCustomer.CustomerZipCode"
            class="customer-col-4"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit ZipCode Label')"
            :error-message="getValidationsMessage('CustomerZipCode')"
            maxlength="50"
            type="text"
            @change="autoComplete"
          />
          <form-input
            id="housenumber"
            v-model.lazy="editCustomer.CustomerHouseNumber"
            class="customer-col-1"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit HouseNumber Label')"
            :error-message="getValidationsMessage('CustomerHouseNumber')"
            maxlength="50"
            type="text"
            @change="autoComplete"
          />
          <form-input
            id="housenumbersuffix"
            v-model.lazy="editCustomer.CustomerHouseNumberAddition"
            class="customer-col-1"
            :title="i18n.t('/Customer/Edit Customer Form/Customer Edit HouseNumberSuffix Label')"
            maxlength="50"
            type="text"
          />
          <loader v-if="addressLoading" size="small" class="customer-col-6" />
          <template v-else>
            <form-input
              id="streetname"
              v-model.lazy="editCustomer.CustomerStreet"
              class="customer-col-3"
              :title="i18n.t('/Customer/Edit Customer Form/Customer Edit StreetName Label')"
              :error-message="getValidationsMessage('CustomerStreet')"
              maxlength="50"
              type="text"
            />
            <form-input
              id="city"
              v-model.lazy="editCustomer.CustomerCity"
              class="customer-col-3"
              :title="i18n.t('/Customer/Edit Customer Form/Customer Edit City Label')"
              :error-message="getValidationsMessage('CustomerCity')"
              maxlength="50"
              type="text"
            />
          </template>
          <div
            v-if="customerAddressInvalid"
            role="region"
            class="customer-col-6 error-message"
            aria-live="polite"
          >
            {{ i18n.t('/Validations/Invalid Address Message') }}
          </div>
          <form-select
            id="country"
            v-model.lazy="editCustomer.CustomerCountryCode"
            :title="`Land`"
            :options="countryList"
            :model-value="`NL`"
            class="customer-col-4"
            @change="autoComplete"
          />
        </app-fieldset>
        <div class="customer-data-block__button-wrapper">
          <app-button
            button-type="submit"
            button-class="primary"
            data-qa="edit-customer-save-button"
          >
            {{ i18n.t('/Common/Save') }}
          </app-button>
        </div>
      </form>
    </modal>
  </span>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import AppFormField from '@/components/generic/presentational/atoms/formField/FormField.vue'
import FormRadioButton from '@/components/generic/presentational/atoms/formRadioButton/FormRadioButton.vue'
import FormSelect from '@/components/generic/presentational/atoms/formSelect/FormSelect.vue'
import CountryHelper from '@/helpers/CountryHelper'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import GenericValidationMessage from '@/helpers/GenericValidationMessage'
import RegexValidator from '@/helpers/RegexValidation'
import { useVuelidate } from '@vuelidate/core'
import { required, email, requiredUnless } from '@vuelidate/validators'
import AddressValidation from '@/api/server/AddressValidation'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'

export default {
  components: {
    Modal,
    FormInput,
    AppFieldset,
    AppFormField,
    FormRadioButton,
    FormSelect,
    AppButton,
    Loader,
  },
  validations() {
    const isValidZipCode = (value) => {
      if (!this.isCustomerAddressInNL) {
        return true
      }
      return RegexValidator(value, '^[1-9][0-9]{3} ?(?!SA|SD|SS|sa|sd|ss)[A-Za-z]{2}$')
    }

    const isValidPhone = (value) => {
      return RegexValidator(value, '[0-9]{10,15}')
    }

    const validAddressesFound = () => {
      return !this.isCustomerAddressInNL || this.foundAddresses === null || this.foundAddresses
    }
    return {
      editCustomer: {
        // either FirstName or Initials is required
        CustomerFirstName: {
          required: requiredUnless((value, model) => {
            return !!model.CustomerInitials
          }),
        },
        CustomerInitials: {
          required: requiredUnless((value, model) => {
            return !!model.CustomerFirstName
          }),
        },
        CustomerFamilyName: {
          required,
        },
        CustomerGender: {
          required,
        },
        CustomerEmail: {
          email,
        },
        CustomerPhone: {
          isValidPhone,
        },
        CustomerStreet: {
          required,
        },
        CustomerHouseNumber: {
          required,
        },
        CustomerZipCode: {
          required,
          isValidZipCode,
        },
        CustomerCity: {
          required,
        },
        CustomerAddress: {
          validAddressesFound,
        },
      },
    }
  },
  props: {
    customer: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    buttonTitle: {
      type: String,
      default: '',
    },
    modalTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['update-customer'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      editCustomer: { ...this.customer },
      countryList: {},
      showModal: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      addressLoading: false,
      foundAddresses: null,
    }
  },
  computed: {
    genderTypes() {
      const genderValues = [
        {
          value: 'Female',
          label: this.i18n.t('/Customer/Create New Customer Form/New Customer Gender Female'),
        },
        {
          value: 'Male',
          label: this.i18n.t('/Customer/Create New Customer Form/New Customer Gender Male'),
        },
        {
          value: 'Unknown',
          label: this.i18n.t('/Customer/Create New Customer Form/New Customer Gender Other'),
        },
      ]

      // Each gender can have multiple values from the backend, like 'Male' or 'M'
      const genderMapping = {
        F: 0,
        FEMALE: 0,
        M: 1,
        MALE: 1,
        U: 2,
        UNKNOWN: 2,
        ['']: 2,
      }

      const genderIndex = genderMapping[this.editCustomer.CustomerGender?.toUpperCase()]

      if (genderIndex !== undefined) {
        genderValues[genderIndex].value = this.editCustomer.CustomerGender
      }

      return genderValues
    },

    openModalButtonTitle() {
      return (
        this.buttonTitle || this.i18n.t('/Customer/Edit Customer Form/Customer Edit Data Label')
      )
    },
    openedModalTitle() {
      return this.modalTitle || this.i18n.t('/Customer/Edit Customer Form/Customer Edit Data Label')
    },
    isCustomerAddressInNL() {
      return this.editCustomer.CustomerCountryCode === 'NL'
    },
    customerAddressInvalid() {
      return this.v$.editCustomer.CustomerAddress.$errors.length > 0
    },
  },
  watch: {
    customer: function (newCustomer) {
      this.editCustomer = { ...newCustomer } || {}
    },
  },
  created() {
    this.countryList = Object.keys(CountryHelper.countryList()).map(function (key) {
      return { name: CountryHelper.countryList()[key], value: key }
    })
  },
  mounted() {
    if (this.editCustomer.CustomerGender == null) {
      // default gender
      this.editCustomer.CustomerGender = 'Unknown'
    }
    if (this.editCustomer.CustomerCountryCode == null) {
      this.editCustomer.CustomerCountryCode = 'NL'
    }
  },
  methods: {
    toggleModal(open) {
      this.showModal = open
      if (!open) {
        this.editCustomer = { ...this.customer } || {}
      } else {
        if (
          !this.isNullOrWhiteSpace(this.editCustomer.CustomerAddress) &&
          this.isNullOrWhiteSpace(this.editCustomer.CustomerStreet)
        ) {
          const parsedAddress = CustomerSearchHelper.parseAddress(this.editCustomer.CustomerAddress)
          this.editCustomer.CustomerStreet = parsedAddress.street
          this.editCustomer.CustomerHouseNumber = parsedAddress.houseNumber
          this.editCustomer.CustomerHouseNumberAddition = parsedAddress.houseNumberAddition
        }
      }
    },
    isNullOrWhiteSpace(input) {
      if (typeof input !== 'string') return true
      return input.trim().length === 0
    },
    updateCustomer() {
      this.v$.editCustomer.$touch()
      if (this.v$.editCustomer.$errors.length > 0) {
        return
      }

      this.editCustomer.CustomerAddress = `${this.editCustomer.CustomerStreet} ${
        this.editCustomer.CustomerHouseNumber
      } ${this.editCustomer.CustomerHouseNumberAddition || ''}`.trim()

      if (this.editCustomer.CustomerMagicNumber) {
        this.editCustomer.CustomerUpdateToAutoline = true
      }

      this.$emit('update-customer', this.editCustomer)
      this.toggleModal(false)
    },
    getValidationsMessage(fieldName) {
      // Specific validationMessages go here below

      const fieldToValidate = this.v$.editCustomer[fieldName]

      if (!fieldToValidate || !fieldToValidate.$errors.length > 0) return

      //specific messages for required validations
      if (!!fieldToValidate.required && !fieldToValidate.required.$response) {
        switch (fieldName) {
          case 'CustomerInitials':
            return this.i18n.t('/Validations/Initials/Required')
          case 'CustomerFirstName':
            return this.i18n.t('/Validations/First Name/Required')
          case 'CustomerFamilyName':
            return this.i18n.t('/Validations/Family Name/Required')
          case 'CustomerStreet':
            return this.i18n.t('/Validations/Street Name/Required')
          case 'CustomerCity':
            return this.i18n.t('/Validations/City/Required')
          case 'CustomerGender':
            return 'Kies een geslacht'
        }
      }

      //Specific messages got regex validations
      if (fieldName == 'CustomerEmail' && !fieldToValidate.email.$response) {
        return this.i18n.t('/Validations/Email/Invalid Format')
      }

      if (fieldName == 'CustomerZipCode' && !fieldToValidate.isValidZipCode.$response) {
        return this.i18n.t('/Validations/ZipCode/Invalid Format')
      }

      if (fieldName == 'CustomerPhone' && !fieldToValidate.isValidPhone.$response) {
        return this.i18n.t('/Validations/Phone/Invalid Format')
      }

      // Else we will find a generic validation message
      return GenericValidationMessage(fieldToValidate)
    },
    async autoComplete() {
      if (!this.v$.editCustomer['CustomerZipCode'].$dirty) {
        this.v$.editCustomer['CustomerZipCode'].$touch()
      }
      if (
        this.isCustomerAddressInNL &&
        this.editCustomer.CustomerZipCode &&
        !this.v$.editCustomer['CustomerZipCode'].$errors.length > 0
      ) {
        // Format postcode with space and uppercase
        this.formatZipCode()
        if (this.editCustomer.CustomerHouseNumber) {
          this.addressLoading = true

          const addresses = await AddressValidation.getAddressByZipcodeAndHousenumber(
            this.editCustomer.CustomerZipCode,
            this.editCustomer.CustomerHouseNumber
          )
          this.foundAddresses = !!addresses

          if (!this.v$.editCustomer['CustomerAddress'].$dirty) {
            this.v$.editCustomer['CustomerAddress'].$touch()
          }

          if (addresses?.length > 0) {
            this.editCustomer.CustomerStreet = addresses[0].Street
            this.editCustomer.CustomerCity = addresses[0].City
          } else {
            this.editCustomer.CustomerStreet = ''
            this.editCustomer.CustomerCity = ''
          }

          this.addressLoading = false
        }
      }
    },

    formatZipCode() {
      if (
        !this.v$.editCustomer['CustomerZipCode'].$errors.length > 0 &&
        this.isCustomerAddressInNL
      ) {
        this.editCustomer.CustomerZipCode = this.editCustomer.CustomerZipCode.toUpperCase().replace(
          /([1-9][0-9]{3})([A-Z]{2})/,
          '$1 $2'
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.edit-customer-wrapper {
  display: contents;
}

.customer {
  :deep(.fields) {
    grid-template-columns: repeat(6, 1fr);
  }
}
.customer-col-1 {
  grid-column: span 1;
}
.customer-col-2 {
  grid-column: span 2;
}

.customer-col-3 {
  grid-column: span 3;
}

.customer-col-4 {
  grid-column: span 4;
}

.customer-col-5 {
  grid-column: span 5;
}

.customer-col-6 {
  grid-column: span 6;
}

.error-message {
  display: block;
  margin-top: 0.25em;
  color: $color-danger;
  font-size: 0.8em;
}

:deep(.radio-button) {
  .label {
    padding-right: 0.5em;
  }

  .radio-button label {
    padding-left: 1.2em;
  }
}
</style>
